export const evaluationData = {
    "ANF": {
        title: "ANF Baseline",
        subtitle: "AUTONOMOUS FUNCTION",
        metrics: [
            { label: "Conflicts", value: 65, max: 100, color: "#ef4444" }, // 65 per 100
            { label: "Anomalies", value: 6.3, max: 10, color: "#f59e0b" } // 6.3%
        ],
        keyStat: "65 Conflicts",
        content: `
            <h3 class="text-2xl font-bold text-gray-800 mb-4">Baseline Performance Analysis</h3>
            <p class="text-gray-600 mb-6 leading-relaxed">
                The baseline Autonomous Network Function (ANF), operating without the NRCF safety layer, exhibited significant operational risks.
            </p>
            
            <div class="grid grid-cols-1 gap-6 mb-8">
                <div class="bg-red-50 p-6 rounded-xl border border-red-100">
                    <h4 class="font-bold text-red-800 text-lg mb-2">Conflict Rate</h4>
                    <p class="text-red-700 mb-2">
                        Without coordination, the ANF generated <span class="font-black text-2xl">65</span> conflicts per 100 activations.
                    </p>
                    <p class="text-sm text-red-600/80">
                        These conflicts arose from simultaneous optimization attempts (e.g., Energy Saving vs. Load Balancing) violating resource constraints.
                    </p>
                </div>

                <div class="bg-amber-50 p-6 rounded-xl border border-amber-100">
                    <h4 class="font-bold text-amber-800 text-lg mb-2">Anomaly Prevalence</h4>
                    <p class="text-amber-700 mb-2">
                        <span class="font-black text-2xl">6.3%</span> of ANF outputs were identified as anomalous.
                    </p>
                    <p class="text-sm text-amber-600/80">
                        Out of 13,113 samples, 828 were flagged as irregular energy-use patterns that could degrade network performance.
                    </p>
                </div>
            </div>
        `
    },
    "DA": {
        title: "Data Analyser",
        subtitle: "DATA INTEGRITY",
        metrics: [
            { label: "Stability", value: 100, max: 100, color: "#2e8b57" }, // p > 0.05
            { label: "Drift", value: 1, max: 10, color: "#88cc88" }, // KL < 0.1
            { label: "Uniformity", value: 100, max: 100, color: "#10b981" } // ANOVA p > 0.05
        ],
        keyStat: "p > 0.05 (Stable)",
        content: `
            <h3 class="text-2xl font-bold text-green-800 mb-4">Data Quality & Stability</h3>
            <p class="text-gray-600 mb-6 leading-relaxed">
                The NRCF-DA validated 40 Key Performance Indicators (KPIs) across 65,564 samples, ensuring high-quality input for decision making.
            </p>

            <div class="space-y-6">
                <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h4 class="font-bold text-gray-800 mb-3">Statistical Stability (Welch's t-test)</h4>
                    <div class="space-y-3">
                        <div class="flex justify-between items-center border-b border-gray-100 pb-2">
                            <span class="text-gray-600">Energy Consumption Meter</span>
                            <span class="font-mono font-bold text-green-600">p = 1.00</span>
                        </div>
                        <div class="flex justify-between items-center border-b border-gray-100 pb-2">
                            <span class="text-gray-600">Low RSRP Samples Rate</span>
                            <span class="font-mono font-bold text-green-600">p = 0.78</span>
                        </div>
                        <div class="flex justify-between items-center">
                            <span class="text-gray-600">Downlink Radio Utilisation</span>
                            <span class="font-mono font-bold text-green-600">p = 0.34</span>
                        </div>
                    </div>
                    <p class="text-xs text-gray-400 mt-3 italic">p > 0.05 indicates no significant difference between training and inference data.</p>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div class="bg-green-50 p-4 rounded-xl border border-green-100">
                        <h5 class="font-bold text-green-800 text-sm mb-1">Regional Uniformity</h5>
                        <p class="text-xs text-green-700">ANOVA p > 0.05 across Urban, Suburban, Rural.</p>
                    </div>
                    <div class="bg-green-50 p-4 rounded-xl border border-green-100">
                        <h5 class="font-bold text-green-800 text-sm mb-1">Energy Stats</h5>
                        <p class="text-xs text-green-700">Mean: 11,752 Wh<br/>SD: 4,301 Wh</p>
                    </div>
                </div>

                <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                    <h4 class="font-bold text-gray-800 mb-3">Outlier Detection</h4>
                    <ul class="list-disc pl-5 text-gray-600 space-y-2">
                        <li><strong class="text-red-500">High Outliers:</strong> PDCCH CCE High Load (25,000+ outliers detected).</li>
                        <li><strong class="text-green-500">Stable:</strong> Downlink Radio Utilisation (0 outliers).</li>
                    </ul>
                </div>
            </div>
        `
    },
    "AR": {
        title: "Result Evaluator",
        subtitle: "ANOMALY DETECTION",
        metrics: [
            { label: "Detection", value: 100, max: 100, color: "#a0522d" },
            { label: "Explainability", value: 90, max: 100, color: "#d2691e" }
        ],
        keyStat: "828 Anomalies",
        content: `
            <h3 class="text-2xl font-bold text-amber-800 mb-4">Anomaly Detection & Explainability</h3>
            <p class="text-gray-600 mb-6 leading-relaxed">
                NRCF-AR employed ANOBIA (Anomaly Detection) and INFEROBIA (Explainable AI) to validate ANF outputs.
            </p>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div class="bg-amber-50 p-5 rounded-xl border border-amber-100">
                    <h4 class="font-bold text-amber-900 mb-2">ANOBIA Findings</h4>
                    <p class="text-sm text-amber-800 mb-2">Identified <span class="font-bold">828 anomalies</span> (6.3%) in energy-use patterns.</p>
                    <div class="w-full bg-amber-200 h-2 rounded-full overflow-hidden">
                        <div class="bg-amber-600 h-full" style="width: 6.3%"></div>
                    </div>
                </div>
                <div class="bg-amber-50 p-5 rounded-xl border border-amber-100">
                    <h4 class="font-bold text-amber-900 mb-2">INFEROBIA Rules</h4>
                    <p class="text-sm text-amber-800">Generated interpretable rules for high-energy cells.</p>
                </div>
            </div>

            <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h4 class="font-bold text-gray-800 mb-3">Derived Rules for High Energy Consumption</h4>
                <div class="flex items-center gap-4 text-sm">
                    <div class="bg-red-50 px-4 py-2 rounded-lg border border-red-100 text-red-700">
                        <strong>Calls</strong> > 114,000
                    </div>
                    <span class="text-gray-400">+</span>
                    <div class="bg-red-50 px-4 py-2 rounded-lg border border-red-100 text-red-700">
                        <strong>DL Latency</strong> > 7.31 ms
                    </div>
                </div>
            </div>
        `
    },
    "EV": {
        title: "Evaluator Scale",
        subtitle: "MATURITY SCORING",
        metrics: [
            { label: "GANFS", value: 10, max: 10, color: "#663399" },
            { label: "MEGABITS", value: 10, max: 10, color: "#9370db" },
            { label: "Accuracy", value: 90, max: 100, color: "#8b5cf6" }
        ],
        keyStat: "10/10 Score",
        content: `
            <h3 class="text-2xl font-bold text-purple-800 mb-4">System Maturity & Accuracy</h3>
            <p class="text-gray-600 mb-6 leading-relaxed">
                The system achieved perfect scores on medical-inspired maturity scales and high predictive accuracy.
            </p>

            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-center">
                <div class="bg-purple-50 p-3 rounded-xl border border-purple-100">
                    <div class="text-2xl font-black text-purple-600 mb-1">10/10</div>
                    <div class="text-[10px] font-bold text-purple-400 uppercase">GANFS</div>
                </div>
                <div class="bg-purple-50 p-3 rounded-xl border border-purple-100">
                    <div class="text-2xl font-black text-purple-600 mb-1">10/10</div>
                    <div class="text-[10px] font-bold text-purple-400 uppercase">MEGABITS</div>
                </div>
                <div class="bg-purple-50 p-3 rounded-xl border border-purple-100">
                    <div class="text-2xl font-black text-purple-600 mb-1">3</div>
                    <div class="text-[10px] font-bold text-purple-400 uppercase">Gleason</div>
                </div>
                <div class="bg-purple-50 p-3 rounded-xl border border-purple-100">
                    <div class="text-2xl font-black text-purple-600 mb-1">90%</div>
                    <div class="text-[10px] font-bold text-purple-400 uppercase">Accuracy</div>
                </div>
            </div>

            <div class="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h4 class="font-bold text-gray-800 mb-3">Top Predictive Features (Gain)</h4>
                <div class="space-y-3">
                    <div>
                        <div class="flex justify-between text-sm mb-1">
                            <span class="font-medium text-gray-700">Max RRC Connections</span>
                            <span class="text-gray-500">6.67 × 10⁹</span>
                        </div>
                        <div class="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                            <div class="bg-purple-500 h-full" style="width: 100%"></div>
                        </div>
                    </div>
                    <div>
                        <div class="flex justify-between text-sm mb-1">
                            <span class="font-medium text-gray-700">Data Available CTR</span>
                            <span class="text-gray-500">3.65 × 10⁹</span>
                        </div>
                        <div class="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                            <div class="bg-purple-500 h-full" style="width: 55%"></div>
                        </div>
                    </div>
                </div>
            </div>
        `
    },
    "NCF": {
        title: "Normative Control",
        subtitle: "CONFLICT RESOLUTION",
        metrics: [
            { label: "Conflicts", value: 5, max: 100, color: "#b22222" }, // Reduced to 5
            { label: "Reduction", value: 92, max: 100, color: "#22c55e" } // 92.3% reduction
        ],
        keyStat: "-92.3% Conflicts",
        content: `
            <h3 class="text-2xl font-bold text-red-800 mb-4">Normative Control & ASANA Sequencing</h3>
            <p class="text-gray-600 mb-6 leading-relaxed">
                The Normative Control Function (NCF), integrated with the <strong>ASANA</strong> sequencing framework, enforces normative constraints to resolve conflicts between autonomous functions.
            </p>

            <div class="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-6 flex items-start gap-3 cursor-pointer hover:bg-blue-100 transition-colors js-navigate-simulation group">
                <div class="mt-1 text-blue-500 group-hover:scale-110 transition-transform">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M13 12H3"/></svg>
                </div>
                <div>
                    <h4 class="font-bold text-blue-900 text-sm group-hover:text-blue-700">See it in Action</h4>
                    <p class="text-xs text-blue-800 mt-1">
                        Switch to the <strong class="uppercase">Simulation Tab</strong> to watch the <strong>ASANA</strong> framework dynamically resolve these conflicts in real-time.
                    </p>
                </div>
            </div>

            <div class="flex items-center justify-center gap-8 mb-8 bg-gradient-to-r from-red-50 to-green-50 p-8 rounded-2xl border border-gray-200">
                <div class="text-center opacity-50">
                    <div class="text-4xl font-black text-red-400 mb-2">65</div>
                    <div class="text-xs font-bold text-red-300 uppercase">Baseline Conflicts</div>
                </div>
                
                <div class="text-gray-400">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 17l5-5-5-5M6 17l5-5-5-5"/></svg>
                </div>

                <div class="text-center transform scale-110">
                    <div class="text-5xl font-black text-green-600 mb-2">5</div>
                    <div class="text-xs font-bold text-green-500 uppercase">NCF Conflicts</div>
                </div>
            </div>

            <div class="bg-green-50 p-6 rounded-xl border border-green-100 text-center">
                <p class="text-green-800 text-lg font-medium">
                    <span class="font-black text-3xl mr-2">92.3%</span> Reduction in Conflicting Activations
                </p>
                <p class="text-xs text-green-600 mt-2 opacity-80">Statistically significant (p < 0.01)</p>
            </div>
        `
    }
};
